import React, { useState, useEffect, useRef, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import HeaderNavNew from "../../HeaderFotter/Header/HeaderNew.js";
import Fotter from "../../HeaderFotter/Fotter/Fotter.js";
import { ColorRing } from "react-loader-spinner";

const QuestionManager = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [connectionError, setConnectionError] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [currentGameId, setCurrentGameId] = useState("game1");
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  const connectWebSocket = useCallback(() => {
    try {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      const wsUrl = `wss://wsgame.visgroup.co`;
      // const wsUrl = `ws://${window.location.hostname}:6789`;
      console.log("Connecting to:", wsUrl);
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("✅ Connected to game server!");
        setConnectionError(false);
        setIsLoading(false);
        setRetryCount(0);
        
        if (isAuthenticated) {
          console.log(`Requesting questions for game ${currentGameId}`);
          ws.send(JSON.stringify({
            type: "get_questions",
            admin: true,
            game_id: currentGameId
          }));
        } else {
          setShowLoginModal(true);
        }
      };

      ws.onclose = (event) => {
        console.log("❌ WebSocket connection closed", event);
        
        if (!ws.intentionalClose && event.code !== 1008 && retryCount < MAX_RETRIES) {
          console.log(`Attempting to reconnect (${retryCount + 1}/${MAX_RETRIES})...`);
          setConnectionError(true);
          setIsLoading(true);
          setRetryCount(prev => prev + 1);
          
          const delay = Math.min(1000 * Math.pow(2, retryCount), 10000);
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(`Reconnecting after ${delay}ms...`);
            connectWebSocket();
          }, delay);
        } else {
          setIsLoading(false);
          setConnectionError(true);
          if (event.code === 1008) {
            setIsAuthenticated(false);
            setShowLoginModal(true);
          }
          if (retryCount >= MAX_RETRIES) {
            toast.error("Không thể kết nối sau nhiều lần thử lại. Vui lòng tải lại trang.");
          }
        }
      };

      ws.onerror = (error) => {
        console.error("❌ WebSocket error:", error);
      };

      ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data);
          console.log("Received websocket data:", data);

          if (data.type === "ping") {
            ws.send(JSON.stringify({ type: "pong" }));
            return;
          }

          if (data.type === "admin_login_response") {
            setIsSubmitting(false);
            if (data.success) {
              setIsAuthenticated(true);
              setShowLoginModal(false);
              toast.success(data.message);
              ws.send(JSON.stringify({
                type: "get_questions",
                admin: true,
                game_id: currentGameId
              }));
            } else {
              toast.error(data.message);
              ws.intentionalClose = true;
              ws.close();
            }
            return;
          }

          if (data.type === "change_password_response") {
            if (data.success) {
              setShowChangePasswordModal(false);
              toast.success(data.message);
            } else {
              toast.error(data.message);
            }
            return;
          }

          if (data.type === "questions_list") {
            if (data.questions && Array.isArray(data.questions)) {
              console.log(`✅ Received ${data.questions.length} questions for game ${currentGameId}`);
              setQuestions(data.questions);
              setIsLoading(false);
            } else {
              console.warn("❌ Invalid questions data received");
              toast.error("Không thể tải danh sách câu hỏi");
            }
            return;
          }

          if (data.type === "game_state") {
            if (data.questions && Array.isArray(data.questions) && 
                JSON.stringify(data.questions) !== JSON.stringify(questions)) {
              console.log(`✅ Updating questions from game state`);
              setQuestions(data.questions);
            }
            setIsLoading(false);
            return;
          }

          if (data.type === "question_added" || 
              data.type === "question_edited" || 
              data.type === "question_deleted") {
            console.log(`Received ${data.type} response:`, data);
            if (data.success) {
              toast.success(data.message);
              setIsLoading(true);
              ws.send(JSON.stringify({
                type: "get_questions",
                admin: true,
                game_id: currentGameId
              }));
              setShowQuestionModal(false);
              setEditingQuestion(null);
            } else {
              console.warn(`❌ ${data.type} failed:`, data.message);
              toast.error(data.message || "Có lỗi xảy ra");
            }
          }
        } catch (err) {
          console.error("❌ Error parsing message:", err);
          toast.error("Có lỗi xảy ra khi xử lý dữ liệu");
        }
      };
    } catch (error) {
      console.error("Error creating WebSocket connection:", error);
      setConnectionError(true);
      setIsLoading(false);
      toast.error("Không thể kết nối đến máy chủ. Vui lòng thử lại sau.");
    }
  }, [currentGameId, isAuthenticated, retryCount]);

  useEffect(() => {
    let mounted = true;

    const initConnection = async () => {
      if (mounted) {
        try {
          await connectWebSocket();
        } catch (error) {
          console.error("Failed to initialize connection:", error);
        }
      }
    };

    initConnection();

    return () => {
      mounted = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.intentionalClose = true;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connectWebSocket]);

  const handleAddQuestion = (questionData) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      toast.error("Mất kết nối đến server");
      return;
    }
    wsRef.current.send(JSON.stringify({
      type: "add_question",
      admin: true,
      game_id: currentGameId,
      ...questionData
    }));
  };

  const handleEditQuestion = (questionData) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      toast.error("Mất kết nối đến server");
      return;
    }
    wsRef.current.send(JSON.stringify({
      type: "edit_question",
      admin: true,
      game_id: currentGameId,
      ...questionData
    }));
  };

  const handleDeleteQuestion = (questionId) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      toast.error("Mất kết nối đến server");
      return;
    }
    if (window.confirm("Bạn có chắc chắn muốn xóa câu hỏi này?")) {
      wsRef.current.send(JSON.stringify({
        type: "delete_question",
        admin: true,
        game_id: currentGameId,
        id: questionId
      }));
    }
  };

  const handleChangePassword = (oldPassword, newPassword) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "change_password",
        old_password: oldPassword,
        new_password: newPassword
      }));
    } else {
      toast.error("Mất kết nối đến server");
    }
  };

  const handleGameChange = (newGameId) => {
    console.log(`Switching to game ${newGameId}`);
    setCurrentGameId(newGameId);
    if (wsRef.current?.readyState === WebSocket.OPEN && isAuthenticated) {
      setIsLoading(true);
      console.log(`Requesting questions for game ${newGameId}`);
      wsRef.current.send(JSON.stringify({
        type: "get_questions",
        admin: true,
        game_id: newGameId
      }));
    }
  };

  const ChangePasswordModal = () => {
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const handleSubmit = (e) => {
      e.preventDefault();
      if (newPassword !== confirmPassword) {
        toast.error("Mật khẩu mới không khớp");
        return;
      }
      handleChangePassword(oldPassword, newPassword);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 p-6 rounded-lg w-[400px]">
          <h3 className="text-xl text-yellow-300 mb-4">Đổi mật khẩu</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-300 mb-2">Mật khẩu cũ:</label>
              <input
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="w-full p-2 bg-gray-700 text-white rounded"
                required
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">Mật khẩu mới:</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full p-2 bg-gray-700 text-white rounded"
                required
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">Xác nhận mật khẩu mới:</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full p-2 bg-gray-700 text-white rounded"
                required
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setShowChangePasswordModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
              >
                Hủy
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500"
              >
                Đổi mật khẩu
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const QuestionModal = ({ onClose, question }) => {
    const [formData, setFormData] = useState(
      question ? {
        id: question.id,
        question: question.question,
        answers: [...question.answers],
        correct: question.correct
      } : {
        question: "",
        answers: ["", "", "", ""],
        correct: "A"
      }
    );

    console.log("Initial formData:", formData);

    const handleSubmit = (e) => {
      e.preventDefault();
      console.log("Submitting formData:", formData);
      if (question) {
        console.log("Editing question:", question.id);
        handleEditQuestion(formData);
      } else {
        console.log("Adding new question");
        handleAddQuestion(formData);
      }
    };

    const handleQuestionChange = (e) => {
      console.log("Question text changed:", e.target.value);
      setFormData({...formData, question: e.target.value});
    };

    const handleAnswerChange = (index, value) => {
      console.log(`Answer ${String.fromCharCode(65 + index)} changed:`, value);
      const newAnswers = [...formData.answers];
      newAnswers[index] = value;
      setFormData({...formData, answers: newAnswers});
    };

    const handleCorrectAnswerChange = (value) => {
      console.log("Correct answer changed:", value);
      setFormData({...formData, correct: value});
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 p-6 rounded-lg w-[500px]">
          <h3 className="text-xl text-yellow-300 mb-4">
            {question ? "Sửa câu hỏi" : "Thêm câu hỏi mới"}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-300 mb-2">Câu hỏi:</label>
              <input
                type="text"
                value={formData.question}
                onChange={handleQuestionChange}
                className="w-full p-2 bg-gray-700 text-white rounded"
                required
              />
            </div>
            {formData.answers.map((answer, index) => (
              <div key={index}>
                <label className="block text-gray-300 mb-2">
                  Đáp án {String.fromCharCode(65 + index)}:
                </label>
                <input
                  type="text"
                  value={answer}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                  className="w-full p-2 bg-gray-700 text-white rounded"
                  required
                />
              </div>
            ))}
            <div>
              <label className="block text-gray-300 mb-2">Đáp án đúng:</label>
              <select
                value={formData.correct}
                onChange={(e) => handleCorrectAnswerChange(e.target.value)}
                className="w-full p-2 bg-gray-700 text-white rounded"
              >
                {["A", "B", "C", "D"].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
              >
                Hủy
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500"
              >
                {question ? "Cập nhật" : "Thêm"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const LoginModal = () => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleLogin = async (e) => {
      e.preventDefault();
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        setIsSubmitting(true);
        wsRef.current.send(JSON.stringify({
          type: "admin_login",
          username: username,
          password: password
        }));
      } else {
        toast.error("Không thể kết nối đến server");
      }
    };

    return (
      <div className="relative inset-0 flex items-center justify-center z-50 h-[65vh]">
        <div className="bg-gray-800 p-6 rounded-lg w-[400px]">
          <h3 className="text-xl text-yellow-300 mb-4">Đăng nhập Admin</h3>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-gray-300 mb-2">Tên đăng nhập:</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full p-2 bg-gray-700 text-white rounded"
                required
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">Mật khẩu:</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-2 bg-gray-700 text-white rounded"
                required            
              />
            </div>
            <div className="flex justify-end mt-4">
              <button
                type="submit"
                className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500`}

              >
                Đăng nhập
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white">
      <HeaderNavNew />
      <div className="container mx-auto px-4 py-8">
        {!isAuthenticated ? (
          showLoginModal && <LoginModal />
        ) : (
          <>
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-4">
                <h1 className="text-3xl font-bold text-yellow-300">Quản lý câu hỏi</h1>
                <select
                  value={currentGameId}
                  onChange={(e) => handleGameChange(e.target.value)}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700"
                >
                  <option value="game1">Game 1</option>
                  <option value="game2">Game 2</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowChangePasswordModal(true)}
                  className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-500"
                >
                  Đổi mật khẩu
                </button>
                <button
                  onClick={() => setShowQuestionModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500"
                  disabled={connectionError}
                >
                  Thêm câu hỏi
                </button>
              </div>
            </div>

            {connectionError ? (
              <div className="text-center py-8">
                <div className="text-red-500 text-xl mb-4">
                  Không thể kết nối đến máy chủ
                </div>
                <button
                  onClick={connectWebSocket}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500"
                >
                  Thử kết nối lại
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {questions.map((question, index) => (
                  <div key={question.id} className="bg-gray-800 p-6 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="text-xl text-yellow-300 mb-4">
                          {index + 1}. {question.question}
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          {question.answers.map((answer, idx) => (
                            <div
                              key={idx}
                              className={`p-4 rounded ${
                                String.fromCharCode(65 + idx) === question.correct
                                  ? 'bg-green-600/30'
                                  : 'bg-gray-700/30'
                              }`}
                            >
                              <span className="text-yellow-500 mr-2">
                                {String.fromCharCode(65 + idx)}:
                              </span>
                              <span className="text-gray-300">{answer}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => {
                            setEditingQuestion(question);
                            setShowQuestionModal(true);
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500"
                        >
                          Sửa
                        </button>
                        <button
                          onClick={() => handleDeleteQuestion(question.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-500"
                        >
                          Xóa
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Fotter />
      <ToastContainer />
      {isLoading && !connectionError && !showLoginModal && (
        <div className="fixed inset-0 bg-[rgb(75_81_99_/_80%)] flex justify-center items-center z-50">
          <ColorRing
            height={70}
            width={70}
            color="#4fa94d"
            ariaLabel="loading-indicator"
          />
        </div>
      )}
      {showQuestionModal && isAuthenticated && (
        <QuestionModal
          onClose={() => {
            setShowQuestionModal(false);
            setEditingQuestion(null);
          }}
          question={editingQuestion}
        />
      )}
      {showChangePasswordModal && isAuthenticated && (
        <ChangePasswordModal />
      )}
    </div>
  );
};

export default QuestionManager; 